import { Role } from '../../store/interfaces/role'
import { LEGACY_DOCTOR_ROLE_PROFILE, LEGACY_ROLE, LegacyRoleCode, SUPPORTED_LEGACY_ROLE_IDS } from './legacy-role.ids'
import { LegacyRoleDomain, toLegacyRoleCode } from './legacy-role.resolver'

/**
 * Legacy API contract (verified by design, confirm per environment):
 * - `doctorRoles[].roleId` / `operatorRoles[].roleId` match `roles.id`
 * - Those IDs are the legacy permission codes (1, 2, 3, 4, 15, 51, 71 domain-dependent, 561)
 *
 * If a deployment uses surrogate IDs, map them in `ROLE_ID_TO_LEGACY_CODE`.
 */
export interface LegacyRoleValidationResult {
  valid: boolean
  roleIds: number[]
  legacyCodes: LegacyRoleCode[]
  unknownRoleIds: number[]
  mismatchedRoleEntities: Array<{ roleId: number; entityId: number }>
}

export function validateLegacyRoleMapping(
  roleIds: number[],
  roles: Role[] = [],
  domain?: LegacyRoleDomain,
): LegacyRoleValidationResult {
  const legacyCodes: LegacyRoleCode[] = []
  const unknownRoleIds: number[] = []
  const mismatchedRoleEntities: Array<{ roleId: number; entityId: number }> = []

  for (const roleId of roleIds) {
    const code = toLegacyRoleCode(roleId, domain)
    if (code == null) unknownRoleIds.push(roleId)
    else legacyCodes.push(code)
  }

  for (const role of roles) {
    const joinRoleId = roleIds.find((id) => id === role.id)
    if (joinRoleId != null && joinRoleId !== role.id) {
      mismatchedRoleEntities.push({ roleId: joinRoleId, entityId: role.id })
    }
    if (roleIds.includes(role.id) && toLegacyRoleCode(role.id, domain) == null) {
      unknownRoleIds.push(role.id)
    }
  }

  const uniqueUnknown = Array.from(new Set(unknownRoleIds))
  const uniqueCodes = Array.from(new Set(legacyCodes))

  return {
    valid: uniqueUnknown.length === 0 && uniqueCodes.length > 0,
    roleIds,
    legacyCodes: uniqueCodes,
    unknownRoleIds: uniqueUnknown,
    mismatchedRoleEntities,
  }
}

/** Reference map for QA: expected legacy role id → label */
export const LEGACY_ROLE_REFERENCE: Record<LegacyRoleCode, string> = {
  [LEGACY_ROLE.DOCTOR_ADMIN]: 'Médico administrador total',
  [LEGACY_ROLE.DOCTOR_SERVICE_DIRECTOR]: 'Médicos directores de servicios',
  [LEGACY_ROLE.DOCTOR_DIRECTOR]: 'Director médico',
  [LEGACY_ROLE.DOCTOR_PROFESSIONAL]: 'Profesional sanitario',
  [LEGACY_ROLE.DOCTOR_SUMMUM_CONTRACT_PAYMENTS]: 'Summum — pagos contratos médicos (permiso 8)',
  [LEGACY_DOCTOR_ROLE_PROFILE.SERVICE_DIRECTOR_NO_SCHEDULE]: 'Médico dirección servicio sin agenda',
  [LEGACY_ROLE.OPERATOR_RACC_ALIAS]: 'Operador histórico (permiso 15)',
  [LEGACY_ROLE.OPERATOR_RACC]: 'Operador raso',
  [LEGACY_ROLE.OPERATOR_ADMIN]: 'Operador administrador',
  [LEGACY_ROLE.OPERATOR_ICAS]: 'Operador ICAS con permisos',
}

export const ALL_SUPPORTED_LEGACY_ROLE_IDS = [...SUPPORTED_LEGACY_ROLE_IDS] as LegacyRoleCode[]
